/**
 * rbacPermissions.ts — settings-page RBAC capability helpers (Issue #243).
 *
 * 真相边界：
 * - 仅在 `permissions` 中显式拥有通配 `*:*` 的会话被视为超级管理员（预置 super_admin
 *   角色的默认权限），可以进入 `/settings` 角色绑定写入入口。普通管理员只读。
 * - 高危预置角色绑定必须额外携带 `approvalNo` 与 `secondFactorResult='passed'`。
 *   该集合与后端 `PRESET_ROLE_CATALOG` (#242) 中的 high/critical 角色保持一致；
 *   一旦后端目录变更，本表必须同步。
 */
import type { ApiComponents } from '@jinzhuan/api-client';

export type PresetRoleKey = ApiComponents['schemas']['PresetRoleDto']['key'];
export type PresetRoleRiskLevel = ApiComponents['schemas']['PresetRoleDto']['riskLevel'];

/**
 * Permission strings that mark a session as super-admin-capable. The backend
 * super_admin role is seeded with `*:*` only; we deliberately do NOT match on
 * the loose `auth.*` family because ordinary admins may also hold scoped
 * `auth.*` perms.
 */
export const SUPER_ADMIN_PERMISSION = '*:*';

/**
 * High-risk preset role keys. Binding/updating these requires `approvalNo` and
 * `secondFactorResult='passed'` per `RoleBindingService` (#242).
 */
export const HIGH_RISK_PRESET_ROLES: ReadonlySet<PresetRoleKey> = new Set<PresetRoleKey>([
  'super_admin',
  'boss',
  'tech_lead',
]);

export function canManageRoleBindings(permissions: readonly string[]): boolean {
  return permissions.includes(SUPER_ADMIN_PERMISSION);
}

export function isHighRiskPresetRole(roleKey: PresetRoleKey): boolean {
  return HIGH_RISK_PRESET_ROLES.has(roleKey);
}

export function isHighRiskRiskLevel(level: PresetRoleRiskLevel): boolean {
  return level === 'high' || level === 'critical';
}
